import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactDOM from "react-dom";

const TYPE_COLORS = {
  DriverName:   { bg: "transparent", border: "#C4C2F4", text: "#B4B4B4", label: "Drivers" },
  VehicleName:  { bg: "transparent", border: "#C4C2F4", text: "#B4B4B4", label: "Vehicles" },
  VehiclePlate: { bg: "transparent", border: "#C4C2F4", text: "#B4B4B4", label: "Vehicles" },
  DepotOU:      { bg: "transparent", border: "#C4C2F4", text: "#B4B4B4", label: "Depots" },
};

// Display order for group headers
const GROUP_ORDER = ["DriverName", "VehicleName", "VehiclePlate", "DepotOU"];

const DEFAULT_COLOR = { bg: "transparent", border: "#C4C2F4", text: "#B4B4B4", label: "Other" };

/**
 * Portal-based dropdown for @mention autocomplete.
 * Results are grouped by NounType with a sticky section header per group.
 * VehicleName and VehiclePlate share a "Vehicles" header.
 *
 * Props:
 *   anchorEl    HTMLElement  — transparent span in overlay at @query position
 *   suggestions Array<{ text, nounType, entitySourceId }>
 *   isLoading   boolean
 *   activeIndex number       — flat index into the suggestions array (-1 for none)
 *   onSelect    (suggestion) => void
 *   onDismiss   () => void
 */
export function MentionDropdown({ anchorEl, suggestions, isLoading, activeIndex, onSelect, onDismiss }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const dropdownRef   = useRef(null);

  // Position relative to anchor — flip above when not enough space below.
  // Uses the actual rendered height so "Searching…" (small) and a full list (tall)
  // both position correctly without over-shooting.
  useEffect(() => {
    if (!anchorEl) return;
    const rect    = anchorEl.getBoundingClientRect();
    const actualH = dropdownRef.current?.offsetHeight ?? 0;
    const GAP     = 4;
    const MIN_W   = 240; // matches minWidth style

    const spaceBelow = window.innerHeight - rect.bottom;
    const top  = spaceBelow >= actualH + GAP
      ? rect.bottom + GAP                       // enough room — place below
      : Math.max(8, rect.top - actualH - GAP);  // not enough — flip above

    const left = Math.min(rect.left, window.innerWidth - MIN_W - 8);

    setPos({ top, left });
  });

  // Dismiss on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onDismiss?.();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onDismiss]);

  // Build grouped structure, respecting GROUP_ORDER.
  // VehicleName + VehiclePlate share one "Vehicles" section header but keep separate items.
  const groups = useMemo(() => {
    if (!suggestions.length) return [];

    // Bucket by nounType, preserving original flat indices for activeIndex matching.
    const buckets = {};
    suggestions.forEach((s, flatIdx) => {
      const key = s.nounType ?? "Other";
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push({ suggestion: s, flatIdx });
    });

    // Sort groups by GROUP_ORDER, then any unknown types at the end.
    const orderedKeys = [
      ...GROUP_ORDER.filter((k) => buckets[k]),
      ...Object.keys(buckets).filter((k) => !GROUP_ORDER.includes(k)),
    ];

    // Merge VehicleName + VehiclePlate under one "Vehicles" header visually,
    // but keep them as separate groups so colours stay correct per item.
    const result = [];
    let vehicleHeaderEmitted = false;

    for (const key of orderedKeys) {
      const isVehicle = key === "VehicleName" || key === "VehiclePlate";
      const colors = TYPE_COLORS[key] ?? DEFAULT_COLOR;

      if (isVehicle) {
        if (!vehicleHeaderEmitted) {
          result.push({ type: "header", label: "Vehicles", color: TYPE_COLORS["VehicleName"] });
          vehicleHeaderEmitted = true;
        }
      } else {
        result.push({ type: "header", label: colors.label, color: colors });
      }

      for (const { suggestion, flatIdx } of buckets[key]) {
        result.push({ type: "item", suggestion, flatIdx });
      }
    }

    return result;
  }, [suggestions]);

  if (!isLoading && suggestions.length === 0) return null;

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      data-mention-dropdown="true"
      style={{
        position:     "fixed",
        top:          pos.top,
        left:         pos.left,
        zIndex:       9999,
        background:   "#ffffff",
        border:       "1px solid #d1d5db",
        borderRadius: "8px",
        boxShadow:    "0 4px 16px rgba(0,0,0,0.14)",
        minWidth:     240,
        maxWidth:     380,
        maxHeight:    320,
        overflowY:    "auto",
        padding:      "4px 0",
        scrollbarWidth: "thin",
        scrollbarColor: "#DCDCDC transparent",
      }}
    >
      {/* Webkit scrollbar styling */}
      <style>{`[data-mention-dropdown]::-webkit-scrollbar{width:5px}[data-mention-dropdown]::-webkit-scrollbar-thumb{background:#DCDCDC;border-radius:4px}[data-mention-dropdown]::-webkit-scrollbar-track{background:transparent}`}</style>
      {isLoading && (
        <div style={{ padding: "8px 14px", color: "#9ca3af", fontSize: 13 }}>
          Searching…
        </div>
      )}

      {!isLoading && groups.map((row, i) => {
        if (row.type === "header") {
          return (
            <div
              key={`header-${i}`}
              style={{
                padding:      "5px 14px 3px",
                fontSize:     11,
                fontWeight:   700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color:         "#B4B4B4",
                background:    "transparent",
                borderTop:     "none",
                marginTop:     i === 0 ? 0 : 16,
                userSelect:    "none",
              }}
            >
              {row.label}
            </div>
          );
        }

        // item row
        const { suggestion: s, flatIdx } = row;
        const isActive = flatIdx === activeIndex;
        const colors   = TYPE_COLORS[s.nounType] ?? DEFAULT_COLOR;

        return (
          <div
            key={`${s.entitySourceId}-${flatIdx}`}
            onMouseDown={(e) => { e.preventDefault(); onSelect(s); }}
            style={{
              display:    "flex",
              alignItems: "center",
              padding:    "6px 14px 6px 18px",
              cursor:     "pointer",
              background: isActive ? "#f0f4ff" : "transparent",
              userSelect: "none",
              fontSize:   14,
              color:      "#111827",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f0f4ff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? "#f0f4ff" : "transparent"; }}
          >
            {/* Colour dot instead of full badge — cleaner inside a group */}
            <span
              style={{
                width:        8,
                height:       8,
                borderRadius: "50%",
                background:   colors.border,
                flexShrink:   0,
                marginRight:  8,
              }}
            />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {s.text}
            </span>
          </div>
        );
      })}
    </div>,
    document.body
  );
}
